import { Injectable, inject, signal, linkedSignal } from '@angular/core';
import { UserScopedStorageService } from '../services/user-scoped-storage.service';

/**
 * Service for managing sidebar navigation state
 *
 * Manages collapsed/expanded state and mobile menu visibility.
 * State is persisted to localStorage for user preference retention.
 */
@Injectable({ providedIn: 'root' })
export class SidebarNavStateService {
  private readonly STORAGE_KEY = 'sidebar-collapsed';
  private readonly MOBILE_MENU_KEY = 'sidebar-mobile-open';

  private readonly userStorage = inject(UserScopedStorageService);

  // Private writable signals
  private readonly _isCollapsed = linkedSignal<boolean>(() => this.userStorage.get(this.STORAGE_KEY) === 'true');
  private readonly _isMobileMenuOpen = signal<boolean>(false);

  // Public readonly accessors
  readonly isCollapsed = this._isCollapsed.asReadonly();
  readonly isMobileMenuOpen = this._isMobileMenuOpen.asReadonly();

  /**
   * Toggle the collapsed state
   */
  toggleCollapsed(): void {
    this._isCollapsed.update(collapsed => !collapsed);
    this.userStorage.set(this.STORAGE_KEY, String(this._isCollapsed()));
  }

  /**
   * Set the collapsed state explicitly
   */
  setCollapsed(collapsed: boolean): void {
    this._isCollapsed.set(collapsed);
    this.userStorage.set(this.STORAGE_KEY, String(collapsed));
  }

  /**
   * Toggle the mobile menu open/closed
   */
  toggleMobileMenu(): void {
    this._isMobileMenuOpen.update(open => !open);
  }

  /**
   * Set the mobile menu state explicitly
   */
  setMobileMenuOpen(open: boolean): void {
    this._isMobileMenuOpen.set(open);
  }

  /**
   * Close the mobile menu (useful when navigating)
   */
  closeMobileMenu(): void {
    this._isMobileMenuOpen.set(false);
  }
}
